/**
 * Entry point for asking the *currently mounted* recovery controller to run.
 *
 * The controller instance lives inside useRuntimeRecovery()'s effect, which is
 * mounted by <AccountDataManager /> in the trade layout. Other code that wants
 * to request a recovery — notably useConnectionRecovery(), which runs under
 * <ConnectionBanner /> in the dashboard layout — is both in a different
 * component and, off the trade route, mounted when no controller exists at all.
 *
 * So this holds a single requester rather than a controller singleton: no
 * timers, observers or in-flight work outlive the controller that owns them,
 * and callers get a truthful "was anyone listening?" answer they can fall back
 * from.
 */
import type { RuntimeHealthSnapshot } from './recovery-controller'
import type { RecoveryReason } from './types'

type RecoveryRequester = (reason: RecoveryReason) => void

let activeRequester: RecoveryRequester | null = null

/** Registers the mounted controller. Returns an unregister function. */
export function registerRecoveryRequester(requester: RecoveryRequester): () => void {
  activeRequester = requester
  return () => {
    // Guarded so a late unmount can't clear a newer controller's registration
    // (React strict-mode double-invoke, or a remount racing the teardown).
    if (activeRequester === requester) activeRequester = null
  }
}

/**
 * Ask the mounted controller to assess and recover.
 *
 * Returns false when no controller is mounted, so the caller can fall back to
 * whatever recovery it can do on its own.
 */
export function requestRuntimeRecovery(reason: RecoveryReason): boolean {
  if (activeRequester === null) return false
  activeRequester(reason)
  return true
}

type SnapshotLogger = () => void

let activeSnapshotLogger: SnapshotLogger | null = null

/** Registers the mounted controller's logSnapshot. Returns an unregister function. */
export function registerSnapshotLogger(logger: SnapshotLogger): () => void {
  activeSnapshotLogger = logger
  return () => {
    if (activeSnapshotLogger === logger) activeSnapshotLogger = null
  }
}

/**
 * Console-logs the mounted controller's current signals (debug-mode/non-prod
 * gated inside logSnapshot itself). Returns false when no controller is
 * mounted (off the trade route, or before AccountDataManager mounts).
 */
export function requestRuntimeSignalsSnapshot(): boolean {
  if (activeSnapshotLogger === null) return false
  activeSnapshotLogger()
  return true
}

type SnapshotGetter = () => RuntimeHealthSnapshot | null

let activeSnapshotGetter: SnapshotGetter | null = null

/** Registers the mounted controller's getSnapshot. Returns an unregister function. */
export function registerSnapshotGetter(getter: SnapshotGetter): () => void {
  activeSnapshotGetter = getter
  return () => {
    if (activeSnapshotGetter === getter) activeSnapshotGetter = null
  }
}

/** The mounted controller's current signals, or null when none is mounted (or in prod). */
export function getRuntimeHealthSnapshot(): RuntimeHealthSnapshot | null {
  return activeSnapshotGetter?.() ?? null
}
